import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch } from "hooks/useAppDispatch";

import { generateCSV } from "utils/csv";
import { downloadFile } from "utils/downloads";
import Papa from "papaparse";
import { removeDuplicatedElements } from "utils/Array";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAddGraduationClassStudentsMutation,
  useAddOrganizationUsersMutation,
  useAddUserMutation,
  useGetOrganizationsLazyQuery,
  useGetUsersQuery,
} from "graphql/__generated__/hooks";
import { registerThunk } from "reduxStore/auth/thunks";
import { ROLES, User } from "types/user";
import { Organization } from "types/organization";
import { AdminRoutes } from "config/routes";
import { useAvailableStudents } from "hooks/useAvailableStudents";
import { normalizeEmail, normalizeImportedUserRow } from "utils/email";

const COLUMNS = ["name", "email", "role"];

type UserData = {
  name: string;
  email: string;
  role?: string;
};
type LoadingData = { current: number; total: number };
type ReportData = {
  success: string[];
  error: string[];
  alreadyExists: string[];
};

export const useUploadCSVUsers = (roles: string[]) => {
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [displayData, setDisplayData] = useState<boolean>(false);
  const [data, setData] = useState<UserData[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState<LoadingData>({
    current: 0,
    total: 0,
  });
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null);
  const [report, setReport] = useState<ReportData>({
    success: [],
    error: [],
    alreadyExists: [],
  });
  const { availableStudents } = useAvailableStudents();
  const { organizationId, graduationClassId } = useParams();
  const selectedData = useRef<string[]>([]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: users } = useGetUsersQuery();
  const [getOrganization, { data: organizationData }] =
    useGetOrganizationsLazyQuery();
  const [addGraduationClassStudents] = useAddGraduationClassStudentsMutation();
  const [addOrgUsers] = useAddOrganizationUsersMutation();
  const usersEmails = useMemo<string[]>(() => {
    return users?.user.map((u) => normalizeEmail(u.email)) || [];
  }, [users]);
  const usersDict = useMemo<{ [k: string]: User }>(() => {
    return (
      users?.user.reduce(
        (acc, user) => {
          const email = normalizeEmail(user.email);
          acc[email] = user as User;
          return acc;
        },
        {} as { [k: string]: User },
      ) || {}
    );
  }, [users]);

  const organizations = useMemo<Organization[]>(() => {
    return (organizationData?.organization as Organization[]) || [];
  }, [organizationData]);

  const backRoute = useMemo<string>(() => {
    return organizationId && graduationClassId
      ? `/organization/${organizationId}/graduation-class/${graduationClassId}/edit`
      : AdminRoutes.User;
  }, [organizationId, graduationClassId]);

  useEffect(() => {
    if (!organizationId) {
      getOrganization();
    }
  }, [organizationId]);

  const [createUser] = useAddUserMutation();

  const handleGraduationClass = async (usersId: string[]) => {
    const dataGraduationClassStudents = usersId.map((user_id) => ({
      student_id: user_id,
      graduation_class_id: graduationClassId,
    }));
    await addGraduationClassStudents({
      variables: { objects: dataGraduationClassStudents },
    });
  };

  const onSubmit = async (sendInvite = false) => {
    setError(null);
    setLoading(true);
    let filteredData: UserData[] = data;
    const dictData: { [k: string]: UserData } = {};
    const emailsAlreadyExists: string[] = [];
    const successElements: string[] = [];
    const errorElements: string[] = [];
    let userIds: string[] = [];
    let newUserIds: string[] = [];

    if (!selectedOrganization && !organizationId) {
      setError("Please select an organization");
      setLoading(false);
      return;
    }

    const orgId = selectedOrganization?.org_id ?? organizationId;

    // handle selected data
    if (selectedData.current.length > 0) {
      data.forEach((d) => {
        dictData[normalizeEmail(d.email)] = normalizeImportedUserRow(d);
      });
      filteredData = selectedData.current.map(
        (email) => dictData[normalizeEmail(email)],
      );
    }

    // filter elements that already exists in DB
    filteredData = filteredData.filter((d) => {
      const email = normalizeEmail(d.email);
      const exists = usersEmails.includes(email);
      if (exists) {
        emailsAlreadyExists.push(email);
        if (usersDict[email]) {
          userIds.push(usersDict[email]?.user_id as string);
        }
      }
      return !exists;
    });

    const usersIdToAddToOrg: string[] = [];
    // upload new users
    setLoadingData({ current: 0, total: filteredData.length });
    for (const data of filteredData) {
      if (!data.email || !data.name) {
        continue;
      }

      const email = normalizeEmail(data.email);
      const name = data.name.trim();
      const role =
        data.role && roles.includes(data.role) ? data.role : ROLES.STUDENT;
      const result = await dispatch(
        registerThunk({
          email,
          name,
          role: role,
          sendInvite,
        }),
      );
      const payload = result.payload as { status: string; user_id: string };

      if (payload.status === "success") {
        successElements.push(email);
        await createUser({
          variables: {
            user_id: payload.user_id,
            email,
            name,
            org_id: orgId,
            role: role,
          },
        });
        newUserIds.push(payload.user_id);
      } else {
        errorElements.push(email);
      }

      if (
        role === ROLES.COACH ||
        role === ROLES.MANAGER ||
        role === ROLES.DIRECTOR
      ) {
        usersIdToAddToOrg.push(payload.user_id);
      }

      setLoadingData((prev) => ({
        current: prev.current + 1,
        total: prev.total,
      }));
    }

    if (usersIdToAddToOrg.length > 0 && orgId) {
      const usersToAddData = usersIdToAddToOrg.map((userId) => ({
        org_id: orgId,
        user_id: userId,
      }));
      await addOrgUsers({
        variables: {
          objects: usersToAddData,
        },
      });
    }

    // set report
    setReport({
      success: successElements,
      error: errorElements,
      alreadyExists: emailsAlreadyExists,
    });

    if (graduationClassId && organizationId) {
      const availableStudentIds = availableStudents.map(
        (student) => student.user_id,
      );
      const listUsersForGraduationClass = [
        userIds.filter((id) => availableStudentIds.includes(id)),
        ...newUserIds,
      ];
      handleGraduationClass(listUsersForGraduationClass.flat());
    }

    setLoading(false);
    setCompleted(true);
  };

  const handleContinue = (file: File) => {
    if (file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        if (e.target?.result) {
          const csvData = e.target.result as string;

          Papa.parse(csvData, {
            header: true,
            complete: (results: any) => {
              const parsedData = results.data
                .filter((d: UserData) => d.email)
                .map((row: UserData) => normalizeImportedUserRow(row));

              // remove duplicated elements
              const parsedDataFiltered = removeDuplicatedElements(
                parsedData,
                "email",
              );

              setData(parsedDataFiltered);
              selectedData.current = parsedDataFiltered.map((d) => d.email);
              setDisplayData(true);
            },
            error: (err: Error) => {
              console.error("Error parsing CSV:", err);
            },
          });
        }
      };

      reader.readAsText(file);
    }
  };

  const handleDownloadClick = () => {
    const data =
      roles?.length > 1
        ? [
            {
              name: "Jane Smith",
              email: "jane.smith@example.com",
              role: "student",
            },
            {
              name: "John Doe",
              email: "john.doe@example.com",
              role: "coach",
            },
          ]
        : [
            { name: "Jane Smith", email: "jane.smith@example.com" },
            { name: "John Doe", email: "john.doe@example.com" },
          ];
    const csvContent = generateCSV(data, COLUMNS);
    downloadFile(csvContent, "user_example.csv", "text/csv;charset=utf-8;");
  };

  const handleOnComplete = () => {
    navigate(backRoute);
  };

  return {
    backRoute,
    completed,
    data,
    displayData,
    error,
    file,
    loading,
    loadingData,
    organizations,
    organizationId,
    report,
    selectedData,
    selectedOrganization,
    setSelectedOrganization,
    setFile,
    handleDownloadClick,
    handleContinue,
    handleOnComplete,
    setDisplayData,
    onSubmit,
  };
};
